import { Component } from "react";

/**
 * Catches render-time crashes so a single bad component shows a recoverable
 * message instead of unmounting the whole app into a blank page.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the real stack visible in the console for debugging.
    console.error("Unhandled render error", error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Navigating to another route clears the error so the user can keep working.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    const message = this.state.error?.message || String(this.state.error);
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-6">
        <div className="w-full max-w-md rounded-md border border-neutral-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-widest text-neutral-500">Something went wrong</p>
          <p className="mt-2 text-sm text-neutral-800">
            This screen hit an unexpected error. Your data is safe. Reload to continue.
          </p>
          <pre className="mt-3 max-h-32 overflow-auto rounded-sm bg-neutral-100 p-2 text-xs text-red-700 whitespace-pre-wrap">
            {message}
          </pre>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-sm px-4 py-2 text-sm font-bold text-white"
              style={{ background: "#FF7A00" }}
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="rounded-sm border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
